import { useMemo, useEffect } from 'react'
import { Environment, Lightformer } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import { ErrorBoundary } from '../components/logic/ErrorBoundary.jsx'
import React from 'react'
import { debugLog } from '../utils/debug.js'

const SHADOW_CONFIG = {
  bias: -0.0001,
  normalBias: 0.05,
  mapSize: 2048,
}

// Magic numbers that were found experimentally
const LIGHT_INTENSITY_FACTOR_DIRECTIONAL = 0.0025
const LIGHT_INTENSITY_FACTOR_POINT = 0.01
const LIGHT_INTENSITY_FACTOR_ENVIRONMENT = .96
const LIGHT_INTENSITY_FACTOR_BACKGROUND = 4



function createEnvironmentElement(worldConfig, hasSceneBackground, hasLights, intensity)
{
  if (worldConfig?.environmentTexture)
  {
    return (
      <>
        <Environment
          files={'/assets/' + worldConfig.environmentTexture}
          background={worldConfig.background !== undefined ? worldConfig.background : true}
          environmentIntensity={intensity}
        />
      </>
    )
  }

  if (worldConfig?.backgroundColor)
  {
    return (
      <>
        <ambientLight intensity={LIGHT_INTENSITY_FACTOR_BACKGROUND * (worldConfig?.environmentIntensity ?? 1)} color={worldConfig.backgroundColor} />
        <color attach="background" args={worldConfig.backgroundColor} />
      </>
    )
  }

  return null
}


/**
 * Hook for scene lighting based on world configuration.
 */
export function useSceneLighting(scene, sceneManager, overrideWorldConfig = null)
{
  const { worldConfig, hasLights } = useMemo(() =>
  {
    let config = overrideWorldConfig
    let foundLights = false
    let foundConfig = null

    // We only need to search for config if we don't have one and don't have a manager
    // However, if we are meant to traverse for lights anyway, we can combine checks.
    const shouldSearchConfig = !config && !sceneManager

    if (scene)
    {
      scene.traverse((obj) =>
      {
        // 1. Process Lights
        if (obj.isLight)
        {
          debugLog('[useSceneLighting] Processing light: ' + obj.name)
          foundLights = true

          // Store original intensity to prevent compounding multiplication on re-renders/hot-reloads
          if (obj.userData.originalIntensity === undefined)
          {
            obj.userData.originalIntensity = obj.intensity
          }

          if (obj.shadow)
          {
            obj.castShadow = true
            obj.shadow.bias = SHADOW_CONFIG.bias
            obj.shadow.normalBias = SHADOW_CONFIG.normalBias
            obj.shadow.mapSize.width = SHADOW_CONFIG.mapSize
            obj.shadow.mapSize.height = SHADOW_CONFIG.mapSize
          }
          if (obj.isDirectionalLight)
          {
            obj.intensity = obj.userData.originalIntensity * LIGHT_INTENSITY_FACTOR_DIRECTIONAL
          }
          else if (obj.isPointLight)
          {
            obj.intensity = obj.userData.originalIntensity * LIGHT_INTENSITY_FACTOR_POINT
          }
        }

        // 2. Search Config (if needed)
        if (shouldSearchConfig && !foundConfig)
        {
          if (obj.userData?.siteProperties && obj.userData.worldConfig)
          {
            const userData = obj.userData
            let bgColor = userData.background_color
            if (bgColor && typeof bgColor === 'object' && '0' in bgColor)
            {
              bgColor = [ bgColor[ 0 ], bgColor[ 1 ], bgColor[ 2 ] ]
            }
            foundConfig = {
              environmentTexture: userData.environment_texture,
              background: userData.background,
              backgroundColor: bgColor,
              environmentIntensity: userData.environment_intensity,
            }
          }
        }
      })
    }

    if (!config)
    {
      config = foundConfig
    }

    if (!config && sceneManager)
    {
      config = sceneManager.getSiteData()?.worldConfig ?? null
    }

    return { worldConfig: config, hasLights: foundLights }

  }, [ scene, sceneManager, overrideWorldConfig ])

  // Calculate effective intensity (shared between Environment and backgroundIntensity)
  const effectiveEnvIntensity = useMemo(() =>
  {
    const raw = worldConfig?.environmentIntensity ?? 1
    // Consolidated factor application
    return raw * LIGHT_INTENSITY_FACTOR_ENVIRONMENT
  }, [ worldConfig?.environmentIntensity ])

  // 2. Set Background Intensity explicitly (updates the visual background brightness)
  useEffect(() =>
  {
    if (scene)
    {
      scene.backgroundIntensity = effectiveEnvIntensity
    }
  }, [ scene, effectiveEnvIntensity ])

  // 3. Process Lights & Rendering
  return useMemo(() =>
  {
    return (
      <ErrorBoundary FallbackComponent={<></>}>
        {createEnvironmentElement(worldConfig, Boolean(scene?.background), hasLights, effectiveEnvIntensity)}
      </ErrorBoundary>
    )
  }, [ scene, worldConfig, hasLights, effectiveEnvIntensity ])
}

export function SceneLighting({ scene, sceneManager, worldConfig })
{
  return useSceneLighting(scene, sceneManager, worldConfig)
}
